import type { StepUpTier, StepUpReason, StepUpAction } from './types';

/**
 * Implemented by whichever `<StepUpProvider>` is mounted (src/mfa/StepUpProvider.tsx). As
 * read on 2026-10-07 there are two mount sites, the signed-in app and the firm's snapshot
 * form, and a page renders one of them.
 * `present` runs the actual ceremony UI — a passkey prompt, falling back to a TOTP
 * code entry — and resolves once the session is elevated enough to satisfy the
 * gate that triggered it, or rejects if the user cancels.
 *
 * Kept as a small interface (rather than importing the React component here) so
 * this module stays framework-agnostic and independently testable — the request
 * interceptor in `client.ts` only needs to call `runStepUpCeremony`, never React.
 */
export interface StepUpController {
  present(token: string, tier: StepUpTier | undefined, reason: StepUpReason | undefined, action?: StepUpAction): Promise<void>;
  /** Switches the OPEN ceremony to the session-entry presentation, keeping its promise and
   *  whatever the user has typed. Called when a `'floor'` refusal joins a ceremony opened
   *  for any other reason (see {@link runStepUpCeremony}). Optional only so test doubles that
   *  never coalesce reasons need not stub it; `StepUpProvider`, the one real controller,
   *  implements it, and `StepUpForeignWorkspace.test.tsx` pins that it does. */
  escalate?(): void;
}

/** The message every cancelled ceremony has always rejected with. */
export const STEP_UP_CANCELLED_MESSAGE = 'Step-up verification was cancelled.';

/**
 * What a ceremony rejects with when the user dismisses it (`StepUpProvider`'s Cancel), and so
 * what `request()` in client.ts propagates to the caller whose read asked for it
 * (steward-app#2023).
 *
 * A subclass of `Error`, with the same message and the same `name` (`'Error'`, inherited and
 * deliberately not overridden) as the plain `Error` it replaced, so every caller that does not
 * look for it sees exactly what it saw before: `instanceof Error` still holds,
 * `instanceof ApiError` still does not, and `userMessage` still returns this message. Only a
 * caller that asks `instanceof StepUpCancelledError` can tell a dismissed ceremony from a
 * failed read. The advisor roster card (`AdvisorClients.tsx`) asks, because "couldn't load" is
 * not true of a read the advisor chose not to verify for (callers census at introduction,
 * 2026-10-02: no other source file inspected the cancellation by message or class).
 * `StepUpProvider.test.tsx` pins the class, the message and the name.
 */
export class StepUpCancelledError extends Error {
  constructor() {
    super(STEP_UP_CANCELLED_MESSAGE);
  }
}

/**
 * Thrown by {@link runStepUpCeremony} when no ceremony controller is registered (the provider
 * has not mounted). A class of its own, with the same message as the bare `Error` it replaced,
 * so default-deny callers (`lib/curatedError.ts`) can let this SPA-authored sentence through by
 * `instanceof` and never by matching its text.
 */
export const STEP_UP_UNAVAILABLE_MESSAGE = 'Step-up verification is not available right now. Please refresh and try again.';
export class StepUpUnavailableError extends Error {
  constructor() {
    super(STEP_UP_UNAVAILABLE_MESSAGE);
  }
}

let controller: StepUpController | null = null;

/**
 * Registered by `<StepUpProvider>` on mount, cleared (`null`) on unmount.
 *
 * Unregistering ALSO drops any `inFlight` latch: if the provider unmounts while a
 * ceremony is open (route teardown / sign-out), the `present()` promise it owns can
 * never settle, so a stale `inFlight` would block every FUTURE ceremony (after a
 * remount) behind a dead promise. Nulling it here lets the next `runStepUpCeremony`
 * start clean. The orphaned promise is left unsettled but detached — nothing in the
 * torn-down tree still awaits it (and sign-out is a full-page reload that wipes this
 * module state entirely anyway).
 */
export function registerStepUpController(next: StepUpController | null): void {
  controller = next;
  if (next === null) inFlight = null;
}

// A ceremony already in flight is shared by every concurrent 403 — a surface
// that fires several gated reads at once (the Dashboard's parallel pillar
// fetches) must show exactly ONE prompt, not one per request. Cleared as soon
// as it settles (success OR failure) so a LATER, independent 403 — even one
// needing a stricter tier, e.g. a `reveal` gate right after a `view` one just
// succeeded — starts a fresh ceremony instead of replaying a stale result.
let inFlight: Promise<void> | null = null;
// The reason the open ceremony is currently PRESENTED with: the opener's, or `'floor'` once
// a floor joiner has escalated it. Meaningful only while `inFlight` is set, and assigned
// afresh each time a ceremony opens, so nothing has to clear it when one settles.
let inFlightReason: StepUpReason | undefined;

/**
 * Runs (or joins) the step-up ceremony. Every caller within the same in-flight
 * window gets the exact same promise; each is responsible for retrying its own
 * original request once this resolves (see the interceptor in client.ts).
 *
 * A caller that JOINS an already-running ceremony gets it regardless of its
 * own `tier` — the ceremony was already started for whichever caller's 403
 * arrived first, and there is no way to raise the TIER of a ceremony already
 * in progress (its presentation can be escalated; see below). If that ceremony
 * turns out not to satisfy the joiner's own (stricter) gate, its retry 403s again and client.ts's interceptor calls
 * this function a second time — by then the first ceremony has settled and
 * `inFlight` is clear, so this starts a fresh, correctly-tiered ceremony.
 * Bounded by client.ts's MAX_STEP_UP_ATTEMPTS (never under-authorizes, never
 * loops forever), but the joiner can see two prompts instead of one — see
 * stepUpCoalescingTiers.test.ts, which pins this exact sequence down.
 *
 * The same sharing applies to `reason`, and the presentation only ever moves toward the
 * wall. Floor first with anything joining is fine: if the floor refused, it really is in
 * effect, so the non-dismissible presentation is right for every caller. Anything else first
 * with a floor joining ESCALATES the open prompt to the wall (`controller.escalate`), so a
 * floor caller never gets a dismissible prompt by arriving second. That matters more since
 * steward-app#1529: a floor on a workspace other than the active one now arrives here as
 * `'operation'` (client.ts, `stepUpPresentationReason`), and an advisor's client-roster card
 * can raise one in the same tick as the advisor's own floored workspace re-locking. Before
 * this, an 'operation' opener left a joining floor dismissible, and cancelling rejected both
 * without a retry (the F18 dead end). Nothing ever de-escalates: a later non-floor joiner
 * leaves the wall standing.
 */
export function runStepUpCeremony(token: string, tier: StepUpTier | undefined, reason?: StepUpReason, action?: StepUpAction): Promise<void> {
  if (!controller) {
    return Promise.reject(new StepUpUnavailableError());
  }
  if (!inFlight) {
    const active = controller;
    inFlightReason = reason;
    inFlight = (action ? active.present(token, tier, reason, action) : active.present(token, tier, reason)).then(notifySucceeded).finally(() => {
      inFlight = null;
    });
  } else if (reason === 'floor' && inFlightReason !== 'floor') {
    inFlightReason = 'floor';
    controller.escalate?.();
  }
  return inFlight;
}

const succeededListeners = new Set<() => void>();

/**
 * Called once each time a ceremony started by {@link runStepUpCeremony} SUCCEEDS (never on a
 * cancel or a failure), after the server has minted the step-up. Returns the unsubscribe.
 *
 * For work that needs a FRESH step-up and must not prompt for one itself: the silent
 * onboarding tax-id copy (crossmarc/appcore#739) needs the reveal tier, which is five minutes,
 * when the household is unfloored (the view tier when it is floored, #1978), so the moment
 * right after a ceremony is the one time it is sure to be inside either window.
 * A listener runs synchronously inside the ceremony's promise chain, so it must only START
 * work, and anything it throws is swallowed here: a listener's bug must never turn a
 * successful ceremony into a rejected one, which would drop the retry of the request that
 * asked for it.
 */
export function onStepUpSucceeded(listener: () => void): () => void {
  succeededListeners.add(listener);
  return () => { succeededListeners.delete(listener); };
}

function notifySucceeded(): void {
  for (const listener of [...succeededListeners]) {
    try { listener(); } catch { /* see onStepUpSucceeded */ }
  }
}

/** True while a ceremony is in flight — exposed for tests only. */
export function __isStepUpInFlightForTests(): boolean {
  return inFlight !== null;
}

/** Test-only reset so suites don't leak a registered controller or in-flight
 *  promise across files. */
export function __resetStepUpForTests(): void {
  controller = null;
  inFlight = null;
  inFlightReason = undefined;
  succeededListeners.clear();
}
